import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { billingAPI } from '../services/api';
import { useBillingStore } from '../stores/billingStore';
import { useAuth } from '../contexts/AuthContext';

// Routes that require tokens to use — gate these if balance is 0
const GATED_ROUTE_PATTERNS = [
  /^\/projects\/[^/]+\/chats/,
  /^\/projects\/[^/]+$/,
];

const isGatedRoute = (pathname) =>
  GATED_ROUTE_PATTERNS.some((pattern) => pattern.test(pathname));

export const useTokenGate = () => {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { openPaywall, paywall } = useBillingStore((s) => s);

  // Scan-to-Play guests run on a fixed allowance — never show them the
  // subscription paywall. Exhaustion is handled in-chat by the backend.
  const isGuest = user?.authProvider === 'guest';

  const { data: billingStatusResponse } = useQuery({
    queryKey: ['billingStatus'],
    queryFn: () => billingAPI.getStatus(),
    enabled: !!isAuthenticated,
    staleTime: 30_000,
  });

  const tokenBalance = billingStatusResponse?.data?.data?.tokenBalance ?? null;

  useEffect(() => {
    if (!isAuthenticated) return;
    if (isGuest) return;
    if (tokenBalance === null) return; // still loading
    if (paywall.isOpen) return; // already showing

    if (isGatedRoute(location.pathname) && tokenBalance === 0) {
      openPaywall({
        isGated: true,
        tokensRemaining: 0,
        tokensRequired: null,
        source: 'gate',
        returnTo: location.state?.previousPath || '/new',
      });
    }
  }, [location.pathname, location.state, tokenBalance, isAuthenticated, isGuest, openPaywall, paywall.isOpen]);
};
